/**
 * Helpers de formatação de data/hora para o admin.
 *
 * Todos os timestamps no backend rodam em UTC (Postgres `timestamptz`, Pino,
 * audit_log). O Brasil opera em BRT (UTC-3, sem horário de verão desde 2019).
 * Os operadores precisam ver os horários em BRT independente de onde
 * abrem o navegador (cloud browser, VPN, viagem, etc.) — não dá pra confiar
 * no fuso do SO.
 *
 * Estes helpers SEMPRE forçam `timeZone: 'America/Sao_Paulo'`, garantindo
 * que o admin mostre os mesmos horários pra todo mundo do time.
 *
 * Aceitam `string | Date | null | undefined`. Quando o input é nulo/inválido,
 * retornam string vazia — UI controla o fallback (ex: "—").
 */

const TZ = 'America/Sao_Paulo';
const LOCALE = 'pt-BR';

/** Normaliza para Date. Retorna `null` se entrada nula ou inválida. */
function toDate(input: string | Date | null | undefined): Date | null {
  if (input === null || input === undefined) return null;
  const d = typeof input === 'string' ? new Date(input) : input;
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Data + hora completos no formato BR: `dd/mm/aaaa, HH:MM:SS`.
 * Use em listas, detalhes e timelines onde precisão importa.
 */
export function formatDateTime(input: string | Date | null | undefined): string {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleString(LOCALE, { timeZone: TZ });
}

/**
 * Só data: `dd/mm/aaaa`. Use em colunas estreitas, filtros, headers.
 */
export function formatDate(input: string | Date | null | undefined): string {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleDateString(LOCALE, { timeZone: TZ });
}

/**
 * Só horário: `HH:MM`. Use em chat bubbles, mensagens, timelines compactas.
 */
export function formatTime(input: string | Date | null | undefined): string {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleTimeString(LOCALE, {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Data + hora compacta: `dd/mm HH:MM`. Use em timelines de mensagens onde
 * já há agrupamento por dia mas o relógio ainda importa.
 */
export function formatDateTimeCompact(input: string | Date | null | undefined): string {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleString(LOCALE, {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formato relativo "há X (min|h|dia)s" pra timestamps recentes.
 * Story 12.3 — usado no banner de handoff pra mostrar "assumida há 12 min".
 *
 * - < 1 min  → "agora mesmo"
 * - < 1 h    → "há N min"
 * - < 24 h   → "há N h"
 * - < 30 d   → "há N dias"
 * - resto    → cai pro formatDateTime absoluto (mais informativo a longo prazo)
 *
 * Sem dependência externa (date-fns não está nas deps do admin).
 */
export function formatRelativeFromNow(
  input: string | Date | null | undefined,
  now: Date = new Date(),
): string {
  const d = toDate(input);
  if (!d) return '';
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) return formatDateTime(d);
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'agora mesmo';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
  return formatDateTime(d);
}
